/**
 * Scheduler Calculation Utilities
 * Pure functions for time and position calculations
 */

/**
 * Calculates the current time position in pixels from the start of the day
 */
export function calculateCurrentTimePosition(
  currentDate: Date,
  startDayHour: number,
  endDayHour: number,
  cellDuration: number,
  totalSlots: number,
  cellHeightPx: number,
  timeZone: string
): number | null {
  try {
    const now = new Date();

    // Convert current time to the specified timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone,
    });

    const parts = formatter.formatToParts(now);
    const tzDate = new Date(
      parseInt(parts.find((p) => p.type === 'year')?.value || '0'),
      parseInt(parts.find((p) => p.type === 'month')?.value || '1') - 1,
      parseInt(parts.find((p) => p.type === 'day')?.value || '1'),
      parseInt(parts.find((p) => p.type === 'hour')?.value || '0'),
      parseInt(parts.find((p) => p.type === 'minute')?.value || '0'),
      parseInt(parts.find((p) => p.type === 'second')?.value || '0')
    );

    // Check if current time is within the scheduler's day view
    const dayStart = new Date(currentDate);
    dayStart.setHours(startDayHour, 0, 0, 0);

    const dayEnd = new Date(currentDate);
    dayEnd.setHours(endDayHour, 0, 0, 0);

    // Only show indicator if current time is within the visible range
    if (tzDate >= dayStart && tzDate < dayEnd) {
      const minutesFromStart =
        (tzDate.getTime() - dayStart.getTime()) / (1000 * 60);
      const pixelsPerMinute =
        (totalSlots * cellHeightPx) / (totalSlots * cellDuration);
      return minutesFromStart * pixelsPerMinute;
    }

    return null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error calculating current time position:', error);
    return null;
  }
}

/**
 * Checks if a time slot is before the current time
 */
export function isBeforeCurrentTime(slot: Date, timeZone: string): boolean {
  try {
    const now = new Date();

    // Convert current time to the specified timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone,
    });

    const parts = formatter.formatToParts(now);
    const tzDate = new Date(
      parseInt(parts.find((p) => p.type === 'year')?.value || '0'),
      parseInt(parts.find((p) => p.type === 'month')?.value || '1') - 1,
      parseInt(parts.find((p) => p.type === 'day')?.value || '1'),
      parseInt(parts.find((p) => p.type === 'hour')?.value || '0'),
      parseInt(parts.find((p) => p.type === 'minute')?.value || '0'),
      parseInt(parts.find((p) => p.type === 'second')?.value || '0')
    );

    return slot < tzDate;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking if slot is before current time:', error);
    // If timezone is invalid, allow context menu (don't restrict)
    return false;
  }
}

/**
 * Calculates the drag offset when an appointment drag starts
 */
export function calculateAppointmentDragOffset(
  event: React.DragEvent<HTMLDivElement>,
  scrollContainer: HTMLDivElement
): number | null {
  const appointmentElement = event.currentTarget;
  if (!appointmentElement) return null;

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = appointmentElement.getBoundingClientRect();
  const elementTopInContainer =
    elementRect.top - containerRect.top + scrollContainer.scrollTop;
  const grabPointInContainer =
    event.clientY - containerRect.top + scrollContainer.scrollTop;

  return grabPointInContainer - elementTopInContainer;
}

/**
 * Calculates the drop slot based on drag offset
 */
export function calculateDropSlot(
  event: React.DragEvent<HTMLDivElement>,
  originalSlot: Date,
  dragOffset: number | null,
  scrollContainer: HTMLDivElement,
  cellHeightPx: number,
  timeSlots: Date[]
): Date {
  if (dragOffset === null) {
    return originalSlot;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const dropPointInContainer =
    event.clientY - containerRect.top + scrollContainer.scrollTop;

  // Calculate where the element's top would be (subtract the grab offset)
  const elementTopAfterDrop = dropPointInContainer - dragOffset;

  // Calculate which slot this position corresponds to
  const slotIndex = Math.floor(elementTopAfterDrop / cellHeightPx);

  // Get the corresponding slot from timeSlots
  if (slotIndex >= 0 && slotIndex < timeSlots.length) {
    return timeSlots[slotIndex];
  }

  return originalSlot;
}
